import type { GameClock } from '@/systems/gameClock';
import { gameClock, klokkeBus } from '@/systems/gameClock';
import type { BakgrunnsMotor, BakgrunnsMotorLagring } from '@/systems/bakgrunnsMotor';
import { bakgrunnsMotor } from '@/systems/bakgrunnsMotor';
import type { RivalLagring, RivalSystem } from '@/systems/rival';
import { rivalen } from '@/systems/rival';
import { gameState } from '@/state/store';

/**
 * Persistens for Mad TV-tilstanden (klokke, bakgrunnsmotor, rival).
 * Lagres som en liten blob i localStorage ved siden av hovedsaven i
 * IndexedDB — tilstanden er liten og endres ved hvert tidsforbruk.
 *
 * Regler ved innlasting:
 * - Rival-tilstand (besøk, bud, hevn, samtaler) gjelder på tvers av dager.
 * - Klokka og dagsloggen gjelder bare hvis lagringen er fra samme spilldag.
 * - Beskjedlapper i resepsjonen består til de kvitteres ut.
 */

export const MADTV_LAGRINGSNOKKEL = 'forlagshuset:madtv:v1';

interface MadTvLagring {
  version: 1;
  dag: number;
  klokkeMinutt: number;
  motor: BakgrunnsMotorLagring;
  rival: RivalLagring;
}

export interface MadTvDeltakere {
  dag: number;
  klokke: GameClock;
  motor: BakgrunnsMotor;
  rival: RivalSystem;
}

export function lagreMadTvTilstand(d: MadTvDeltakere): void {
  if (typeof localStorage === 'undefined') return;
  try {
    const blob: MadTvLagring = {
      version: 1,
      dag: d.dag,
      klokkeMinutt: d.klokke.naa,
      motor: d.motor.tilLagring(),
      rival: d.rival.tilLagring(),
    };
    localStorage.setItem(MADTV_LAGRINGSNOKKEL, JSON.stringify(blob));
  } catch (err) {
    console.warn('[madtv] lagring feilet', err);
  }
}

export function lastMadTvTilstand(d: MadTvDeltakere): void {
  if (typeof localStorage === 'undefined') return;
  try {
    const raw = localStorage.getItem(MADTV_LAGRINGSNOKKEL);
    if (!raw) return;
    const lagret = JSON.parse(raw) as Partial<MadTvLagring>;
    if (lagret.version !== 1) return;
    const sammeDag = lagret.dag === d.dag;
    if (lagret.rival) d.rival.lastFraLagring(lagret.rival);
    if (lagret.motor) d.motor.lastFraLagring(lagret.motor, sammeDag);
    if (sammeDag && typeof lagret.klokkeMinutt === 'number') {
      d.klokke.settMinutt(lagret.klokkeMinutt);
    }
  } catch (err) {
    console.warn('[madtv] innlasting feilet, starter ferskt', err);
  }
}

/** Lagrer øktas singletons umiddelbart (brukes ved dagsskifte). */
export function lagreMadTvNaa(): void {
  lagreMadTvTilstand({
    dag: gameState.dag,
    klokke: gameClock,
    motor: bakgrunnsMotor,
    rival: rivalen,
  });
}

/** Laster øktas singletons fra lagring — kalles én gang ved oppstart. */
export function lastMadTvNaa(): void {
  lastMadTvTilstand({
    dag: gameState.dag,
    klokke: gameClock,
    motor: bakgrunnsMotor,
    rival: rivalen,
  });
}

/**
 * Kobler automatisk lagring til klokke-hendelsene (debounce 150 ms,
 * samme rytme som hovedsaven). Returnerer en frakoblingsfunksjon.
 */
export function koblOppMadTvLagring(): () => void {
  let timer: number | undefined;
  const planlegg = (): void => {
    if (typeof window === 'undefined') return;
    if (timer !== undefined) window.clearTimeout(timer);
    timer = window.setTimeout(() => lagreMadTvNaa(), 150);
  };
  const av1 = klokkeBus.on('time:advanced', planlegg);
  const av2 = klokkeBus.on('time:dayEnded', planlegg);
  return () => {
    av1();
    av2();
    if (typeof window !== 'undefined' && timer !== undefined) {
      window.clearTimeout(timer);
    }
  };
}
